/**
 * PiezoWorkerPool — a bounded pool of Web Workers that sign Piezo artifacts in parallel.
 *
 * Signing is slow, so doing a page's worth of them on the main thread freezes the UI. Each worker is
 * long-lived: it hydrates its own PiezoClient from the IndexedDB cache the main thread wrote, then signs
 * jobs handed to it. If the pool cannot serve at all, `sign()` rejects and callers fall back to signing on
 * the main thread.
 *
 * This file owns worker LIFECYCLE: how many to start, when to give up waiting on one, when to replace it,
 * and teardown. Which worker runs which sign lives in signDispatcher.ts, so neither has to be read through
 * the other.
 *
 * A worker's start-up is dominated by loading its chunk (hundreds of ms to tens of seconds), not by WASM,
 * and varies by an order of magnitude with machine load. Hence the INIT deadline reports rather than kills,
 * and every deadline below is paired with a bounded consequence.
 */

import { PiezoDebugPrefix } from '../provisioning/PiezoClient.constants';
import type { PiezoGetPiezoExecutionContextInput } from '../provisioning/PiezoClient.types';
import { createSignWorker } from './signWorkerFactory';
import { SignDispatcher } from './signDispatcher';
import type { DispatcherHost, PoolWorker } from './signDispatcher';
import { MidwayAuthRequiredError } from './signWorkerProtocol';
import type { PoolToWorker, WorkerToPool } from './signWorkerProtocol';
import { getSharedPiezoWasmModule } from '../wasm/sharedModule';

/**
 * Per-worker INIT deadline. It stops warm-up WAITING on a worker; it does not kill it. Without it a silent
 * worker never settles its spawn promise, so warm-up can never conclude and the "pool cannot serve"
 * decision can never be made. A worker past this is usually still loading, not wedged.
 */
export const INIT_TIMEOUT_MS = 15_000;
/**
 * How much longer past INIT_TIMEOUT_MS a written-off worker is left running before it is reaped and
 * replaced. It is not in `this.workers`, so nothing else would ever release its WASM heap.
 */
export const INIT_LATE_JOIN_GRACE_MS = 15_000;
/** Pool size ceiling, to bound memory: one WASM heap per worker. */
const MAX_WORKERS = 8;
const DEFAULT_CONCURRENCY = 4;
/**
 * Replacement workers allowed = pool size x this, spent once per replacement and never refilled. A worker
 * that keeps dying gets a few retries, then the pool shrinks instead of respawning forever.
 */
const RESPAWN_BUDGET_MULTIPLIER = 3;
/** Swallows the throw: teardown terminates in a loop, where one would skip settling the queue. */
function terminateWorker(worker: Worker): void {
  try {
    worker.terminate();
  } catch {
    /* discarded either way */
  }
}

export class PiezoWorkerPool implements DispatcherHost {
  /** Routing lives here; this class only decides which workers it is given. */
  private readonly dispatcher = new SignDispatcher(this);
  private warmUpPromise?: Promise<void>;
  /**
   * True once warm-up has finished, whichever way. With no workers, this distinguishes "still starting,
   * wait" from "nothing is coming, sign on the main thread".
   */
  private warmUpSettled = false;
  private region?: string;
  /** Only for log labels. Never reset, so a re-warm gets fresh numbers and the two are distinguishable. */
  private nextSpawnId = 0;
  /** Worker count to restore when replacing a lost worker. */
  private desiredSize = 0;
  private respawnBudget = 0;
  /**
   * Whether any worker has ever reported READY. Distinguishes "this pool worked and lost a worker", where a
   * replacement is worth spawning, from "nothing here has ever started", where it repeats a failed
   * experiment in the same environment and only delays the fallback.
   */
  private everReady = false;
  /** Kept from warm-up so a replacement worker skips the compile too. undefined = none was produced. */
  private sharedWasmModule?: WebAssembly.Module;
  /**
   * Set once postMessage has refused to clone a WebAssembly.Module. That is a property of the engine, not of
   * a worker, so the refusal is permanent and no later spawn should re-attempt it.
   */
  private wasmPostRefused = false;
  /**
   * Bumped by terminate(). Anything that outlives a teardown compares its snapshot and bails rather than
   * acting on the pool that replaced it — an adopted orphan would hold a WASM heap for the life of the tab.
   */
  private generation = 0;
  /**
   * Release callbacks for workers started but not yet settled into (or out of) `this.workers`. One written
   * off at its INIT deadline keeps running, so this is the only handle teardown has on it.
   */
  private readonly inFlightWorkers = new Set<() => void>();

  /**
   * Spawn the workers and INIT each one. Idempotent — every caller shares the first warm-up.
   *
   * Waits on `initPromise` because workers only read the IndexedDB cache and cannot populate it. `sizeHint`
   * caps the worker count; falsy means use the machine's, which is one less than its core count.
   *
   * Finishing does not release queued work on its own: workers written off at the INIT deadline are still
   * running, and on a loaded machine every one of them misses it. Dumping the queue here would send a whole
   * page-load burst to the main thread at 15s while those workers report READY seconds later and idle. The
   * queue is released only once nothing is ready AND nothing is starting, which the reap chain reaches.

   */
  public warmUp(initPromise: Promise<unknown>, region: string, sizeHint: number): Promise<void> {
    if (this.warmUpPromise) {
      return this.warmUpPromise;
    }
    this.region = region;
    // terminate() cannot stop this body, only the spawns it already started, so a superseded warm-up
    // checks the generation at each point it resumes rather than acting on the pool that replaced it.
    const generationAtWarmUp = this.generation;
    this.warmUpPromise = (async () => {
      const sharedWasm = getSharedPiezoWasmModule();
      await initPromise; // the cache must exist before any worker tries to read it
      const wasmModule = await sharedWasm;
      if (generationAtWarmUp !== this.generation) {
        throw new Error('PiezoWorkerPool: warm-up superseded by a teardown');
      }
      const cores =
        (typeof navigator !== 'undefined' && navigator.hardwareConcurrency) || DEFAULT_CONCURRENCY;
      const usableCores = Math.max(1, cores - 1);
      const size = Math.min(usableCores, MAX_WORKERS, sizeHint || usableCores);
      this.desiredSize = size;
      this.respawnBudget = size * RESPAWN_BUDGET_MULTIPLIER;
      this.sharedWasmModule = wasmModule;
      const results = await Promise.allSettled(
        Array.from({ length: size }, () => this.spawnOne(region, wasmModule)),
      );
      const missed = results.filter(
        (result): result is PromiseRejectedResult => result.status === 'rejected',
      );
      if (missed.length > 0) {
        // The reasons matter: a worker can also reject with INIT_ERROR, whose message names the real cause
        // (a missing cache, for instance). Blaming the deadline for all of them discards the only
        // diagnostic a total warm-up failure produces.
        const reasons = Array.from(new Set(missed.map((result) => String(result.reason))));
        console.warn(
          `${PiezoDebugPrefix} PiezoWorkerPool: ${missed.length}/${size} worker(s) did not join; ` +
            `continuing with ${this.dispatcher.workerCount()}, ${this.inFlightWorkers.size} still ` +
            `starting — ${reasons.join(' | ')}`,
        );
      }
      if (!this.dispatcher.isReady() && this.inFlightWorkers.size === 0) {
        throw new Error(`All ${size} Piezo sign workers failed to init`);
      }
    })().then(
      () => this.settleWarmUp(generationAtWarmUp, false, undefined),
      (err) => this.settleWarmUp(generationAtWarmUp, true, err),
    );
    return this.warmUpPromise;
  }

  /**
   * Ends a warm-up. A superseded one neither tears down nor settles the pool; a failed one terminates.
   *
   * `failed` is separate from `err` because a promise can reject with undefined.
   */
  private settleWarmUp(generationAtWarmUp: number, failed: boolean, err: unknown): void {
    if (generationAtWarmUp !== this.generation) {
      if (failed) {
        throw err; // still reject this warm-up's own promise; just do not touch the newer pool
      }
      return;
    }
    if (failed) {
      console.warn(`${PiezoDebugPrefix} PiezoWorkerPool warm-up failed: ${String(err)}`);
      this.terminate();
      throw err;
    }
    this.warmUpSettled = true;
    this.dispatcher.failQueuedIfStranded();
  }

  /** Whether a sign can be dispatched right now. Answered by the dispatcher, which owns the worker list. */
  public isReady(): boolean {
    return this.dispatcher.isReady();
  }

  /** DispatcherHost: true while a worker is still starting and could yet report READY. */
  public hasWorkersStarting(): boolean {
    return this.inFlightWorkers.size > 0;
  }

  /**
   * DispatcherHost: the dispatcher has stopped routing to this worker. Terminate it, replace it if that is
   * warranted, and only then ask about the queue -- a replacement counts as "something starting", so
   * checking before it exists would release work that a new worker is about to be able to run.
   */
  public workerLost(pw: PoolWorker, cause: Error, wasRouting: boolean): void {
    terminateWorker(pw.worker);
    if (wasRouting) {
      console.warn(`${PiezoDebugPrefix} PiezoWorkerPool: lost ${pw.label} — ${cause.message}`);
      this.respawn();
    }
    this.dispatcher.failQueuedIfStranded();
  }

  /**
   * Queue one sign. Rejects on a per-sign failure, an expired Midway session, or a pool that will never
   * serve; the caller turns any rejection into main-thread signing.
   *
   * Does not wait for readiness: a job with no worker yet waits in the queue for the first one to arrive.
   */
  public sign(input: PiezoGetPiezoExecutionContextInput): Promise<string> {
    if (!this.warmUpPromise) {
      return Promise.reject(new Error('PiezoWorkerPool: no warm-up was started'));
    }
    if (this.warmUpSettled && !this.dispatcher.isReady() && this.inFlightWorkers.size === 0) {
      return Promise.reject(new Error('PiezoWorkerPool: warm-up finished with no usable worker'));
    }
    return this.dispatcher.submit(input);
  }

  /**
   * Replace a lost worker, within budget and only while the pool is below strength. Skipped entirely if no
   * worker has ever started: the replacement would face the same environment and only delay the fallback.
   *
   * Declining is itself a transition worth checking: both callers -- an evicted worker and a reaped one --
   * can leave the queue with nowhere to go.
   */
  private respawn(): void {
    const neverWorked = this.warmUpSettled && !this.everReady;
    if (
      !this.region ||
      neverWorked ||
      this.respawnBudget <= 0 ||
      this.dispatcher.workerCount() >= this.desiredSize
    ) {
      this.dispatcher.failQueuedIfStranded();
      return;
    }
    this.respawnBudget--;
    void this.spawnOne(this.region, this.sharedWasmModule)
      .catch((err) =>
        console.warn(`${PiezoDebugPrefix} PiezoWorkerPool: replacement worker failed: ${String(err)}`),
      )
      .finally(() => this.dispatcher.failQueuedIfStranded());
  }

  /**
   * Create one worker, INIT it, and hand it to the dispatcher once it reports READY. `wasmModule` lets it
   * skip its own ~4.6 MB compile; omitted is fine, it compiles its own.
   */
  private spawnOne(region: string, wasmModule?: WebAssembly.Module): Promise<void> {
    const worker = createSignWorker();
    const label = `worker[${this.nextSpawnId++}]`;
    const pw: PoolWorker = { worker, label, inFlight: new Map() };
    worker.addEventListener('message', (event: MessageEvent<WorkerToPool>) =>
      this.dispatcher.handleWorkerMessage(pw, event.data),
    );
    worker.addEventListener('error', () => {
      const cause = new Error(`${label} crashed`);
      this.workerLost(pw, cause, this.dispatcher.removeWorker(pw, cause));
    });
    const generationAtSpawn = this.generation;

    return new Promise<void>((resolve, reject) => {
      let initTimer: ReturnType<typeof setTimeout> | undefined;
      let lateJoinTimer: ReturnType<typeof setTimeout> | undefined;
      /** Stop waiting on INIT. Safe before the timers exist — clearTimeout(undefined) is a no-op. */
      const stopWaiting = () => {
        this.inFlightWorkers.delete(release);
        clearTimeout(initTimer);
        clearTimeout(lateJoinTimer);
        worker.removeEventListener('message', onReady);
        worker.removeEventListener('error', onInitError);
      };
      const kill = () => terminateWorker(worker);
      /**
       * Give up on this worker and free it. Resolves rather than rejects because the reasons for calling it
       * -- a teardown, or a worker that never answered -- are cancellations, not failures of this spawn. If
       * the spawn already rejected at its INIT deadline this is a no-op on the promise.
       */
      const release = () => {
        stopWaiting();
        kill();
        resolve();
      };
      const onInitError = () => {
        stopWaiting();
        reject(new Error(`${label} errored during init`));
      };
      const onReady = (event: MessageEvent<WorkerToPool>) => {
        if (event.data.type === 'READY') {
          stopWaiting();
          if (generationAtSpawn !== this.generation) {
            kill();
            resolve();
            return;
          }
          this.everReady = true;
          this.dispatcher.addWorker(pw);
          resolve();
        } else if (event.data.type === 'INIT_ERROR') {
          stopWaiting();
          kill();
          reject(new Error(event.data.message));
        }
      };

      initTimer = setTimeout(() => {
        reject(
          new Error(
            `${label} INIT not ready after ${INIT_TIMEOUT_MS}ms; left running in case it joins late`,
          ),
        );
        lateJoinTimer = setTimeout(() => {
          console.warn(
            `${PiezoDebugPrefix} PiezoWorkerPool: ${label} never reported READY within ` +
              `${INIT_TIMEOUT_MS + INIT_LATE_JOIN_GRACE_MS}ms; terminating and replacing it`,
          );
          release();
          this.respawn();
        }, INIT_LATE_JOIN_GRACE_MS);
      }, INIT_TIMEOUT_MS);
      this.inFlightWorkers.add(release);
      worker.addEventListener('message', onReady);
      worker.addEventListener('error', onInitError);
      let sentWithModule = false;
      if (wasmModule !== undefined && !this.wasmPostRefused) {
        try {
          const init: PoolToWorker = { type: 'INIT', region, label, wasmModule };
          worker.postMessage(init);
          sentWithModule = true;
        } catch (err) {
          this.wasmPostRefused = true;
          this.sharedWasmModule = undefined;
          console.warn(
            `${PiezoDebugPrefix} PiezoWorkerPool: this browser will not accept the shared WASM module ` +
              `(${String(err)}); every worker will compile its own copy`,
          );
        }
      }
      if (!sentWithModule) {
        const init: PoolToWorker = { type: 'INIT', region, label };
        worker.postMessage(init);
      }
    });
  }

  /**
   * Tear the pool down. Releases workers still starting before terminating the ready ones: a worker written
   * off at its INIT deadline is not in the dispatcher's list, so this is teardown's only handle on it.
   */
  public terminate(): void {
    this.generation++;
    const terminated = new Error('Pool terminated');
    for (const release of Array.from(this.inFlightWorkers)) {
      release();
    }
    this.inFlightWorkers.clear();
    for (const pw of this.dispatcher.activeWorkers()) {
      terminateWorker(pw.worker);
    }
    this.dispatcher.shutDown(terminated);
    this.warmUpPromise = undefined;
    this.warmUpSettled = false;
    this.desiredSize = 0;
    this.respawnBudget = 0;
    this.everReady = false;
    this.sharedWasmModule = undefined;
  }
}

export { MidwayAuthRequiredError };

/** Module-level singleton, mirroring PiezoClient's cachedPiezoClient pattern. */
let cachedPool: PiezoWorkerPool | undefined;
export function getPiezoWorkerPool(): PiezoWorkerPool {
  if (!cachedPool) {
    cachedPool = new PiezoWorkerPool();
  }
  return cachedPool;
}
